import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

interface ContaRecorrenteFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  title: string;
}

export function ContaRecorrenteForm({ initialData, onSubmit, onCancel, title }: ContaRecorrenteFormProps) {
  const [formData, setFormData] = useState({
    nome: initialData?.nome || "",
    categoria: initialData?.categoria || "Despesas Fixas",
    valor: initialData?.valor || "",
    dia_vencimento: initialData?.dia_vencimento || "1",
    ativo: initialData?.hasOwnProperty('ativo') ? initialData.ativo : true,
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      valor: parseFloat(formData.valor),
      dia_vencimento: parseInt(formData.dia_vencimento)
    });
  };

  const categorias = ["Despesas Fixas", "Despesas Variáveis", "Fornecedores", "Materiais", "Outros"];

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-lg mx-auto shadow-2xl animate-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-white/5">
          <X size={24} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Nome da Conta</Label>
          <Input 
            value={formData.nome}
            onChange={(e) => handleChange("nome", e.target.value)}
            placeholder="Ex: Aluguel, Luz, Internet"
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Categoria</Label>
            <Select 
              value={formData.categoria} 
              onValueChange={(v) => handleChange("categoria", v)}
              required
            >
              <SelectTrigger className="bg-background border-border focus:ring-primary h-11">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                {categorias.map(cat => (
                  <SelectItem key={cat} value={cat} className="focus:bg-primary focus:text-white">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Valor</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
              <Input 
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => handleChange("valor", e.target.value)}
                placeholder="0,00"
                className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 pl-10"
                required
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Dia de Vencimento</Label>
          <Input 
            type="number"
            min="1"
            max="31"
            value={formData.dia_vencimento}
            onChange={(e) => handleChange("dia_vencimento", e.target.value)}
            placeholder="Ex: 10"
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11"
            required
          />
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
          <div className="space-y-0.5">
            <Label className="text-sm font-normal">Ativa?</Label>
            <p className="text-xs text-muted-foreground">As contas inativas não geram lançamentos</p>
          </div>
          <Switch 
            checked={formData.ativo}
            onCheckedChange={(v) => handleChange("ativo", v)}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            type="submit" 
            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12 transition-all"
          >
            Salvar Conta Recorrente
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="px-6 border-border bg-transparent text-[#A1A1AA] hover:bg-white/5 hover:text-foreground h-12 transition-all"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
